<script>
import { maxLength } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'

const MAX_HIGHLIGHTS = 4

const HIGHLIGHT_ERROR_MESSAGES = {
  too_many_thematic_pages: 'pages.config:error.too.many',
  thematic_page_not_found: 'pages.config:error.not.found',
  ordenation_required: 'pages.config:error.ordenation.required',
  ordenation_values_are_default_or_manual: 'pages.config:error.ordenation.invalid'
}

export default {
  name: 'PagesConfig',
  components: {
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Tabs: () => import('@/components/general/Tabs'),
    Datatable: () => import('@/components/general/Datatable'),
    Action: () => import('@/components/general/Action'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    FormSection: () => import('@/components/general/FormSection'),
    Divisor: () => import('@/components/general/Divisor'),
    Icon: () => import('@/components/general/Icon'),
    Radio: () => import('@/components/general/Radio'),
    InputField: () => import('@/components/general/InputField'),
    ThematicPageHeaderPreview: () => import('@/modules/ThematicPages/components/ThematicPageHeaderPreview.vue')
  },

  data () {
    return {
      maxHighlights: MAX_HIGHLIGHTS,

      activeSubTab: 0,

      subTabLinks: [
        { text: this.$t('pages.config:subtab.texts') },
        { text: this.$t('pages.config:subtab.highlights') }
      ],

      headerData: {
        title: '',
        subtitle: ''
      },

      formData: {
        listHighlights: [],
        ordenation: 'default',
        title: '',
        description: ''
      },

      tabLinks: [
        {
          location: { name: 'thematicPages.index' },
          text: this.$t('thematicPages:tabs.link.1')
        },
        {
          location: { name: 'pages.configs' },
          text: this.$t('solutions.tabs:link.2')
        }
      ],

      orderOptions: [
        {
          label: this.$t('global:manual'),
          value: 'manual'
        },
        {
          label: this.$t('global:default'),
          value: 'default'
        }
      ],

      pagesPagination: {
        query: {
          name: null,
          limit: 300
        },

        filter: {
          active: 1,
          showonvitrine: 1
        },

        order: {
          name: 'ASC'
        },

        lastPage: null,
        limit: 6,
        page: 1
      },

      isLoadingMorePages: false,
      pages: []
    }
  },

  validations: {
    headerData: {
      title: {
        maxLength: maxLength(100)
      },

      subtitle: {
        maxLength: maxLength(500)
      }
    },

    formData: {
      listHighlights: {
        maxLength: maxLength(MAX_HIGHLIGHTS)
      },

      title: {
        maxLength: maxLength(100)
      },

      description: {
        maxLength: maxLength(500)
      }
    }
  },

  computed: {
    availablePages () {
      return this.pages.filter(page => !this.formData.listHighlights.find(highlight => page.id === highlight.id))
    }
  },

  created () {
    this.setFetching(true)

    Promise.all([
      this.fetchSettings(),
      this.fetchThematicPagesData()
    ]).finally(() => {
      this.setFetching(false)
    })
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetEligibleThematicPages',
      'attemptGetThematicPagesGeneralSettings',
      'attemptGetListThematicPagesHighlights',
      'attemptUpdateThematicPagesHighlights',
      'attemptUpdateThematicPagesHeader'
    ]),

    fetchSettings () {
      this.setFetching(true)

      return this.attemptGetThematicPagesGeneralSettings().then(({ data }) => {
        const byField = (field) => data.data.find((item) => item.field === field)

        const headerTitle = byField('thematic_page_header_title')
        const headerSubtitle = byField('thematic_page_header_subtitle')
        const ordenation = byField('thematic_page_highlight_ordenation')
        const title = byField('thematic_page_highlight_title')
        const description = byField('thematic_page_highlight_description')

        this.headerData.title = headerTitle && headerTitle.value ? headerTitle.value : ''
        this.headerData.subtitle = headerSubtitle && headerSubtitle.value ? headerSubtitle.value : ''
        this.formData.ordenation = ordenation && ordenation.value ? ordenation.value : 'default'
        this.formData.title = title && title.value ? title.value : ''
        this.formData.description = description && description.value ? description.value : ''

        return this.attemptGetListThematicPagesHighlights().then(({ data }) => {
          this.formData.listHighlights = data.data.map((highlight) => {
            return {
              active: 1,
              ...highlight.thematicPage
            }
          })

          if (this.formData.ordenation === 'manual' && this.formData.listHighlights.length === 0) {
            this.formData.ordenation = 'default'
          }

          this.setFetching(false)
        })
      })
    },

    fetchThematicPagesData (isLoadMore = false) {
      this.isLoadingMorePages = true

      return this.attemptGetEligibleThematicPages(this.pagesPagination)
        .then((pagination) => {
          this.pagesPagination.lastPage = pagination.lastPage
          this.pagesPagination.page = pagination.actualPage

          if (pagination.data) {
            if (!isLoadMore) {
              this.pages = []
            }

            pagination.data.forEach(element => {
              this.pages.push({
                name: element.name,
                id: element.id,
                thematicPageType: element.thematicPageType,
                showOnVitrine: element.showOnVitrine
              })
            })
          }

          this.isLoadingMorePages = false

          return pagination
        })
    },

    searchThematicPage (search) {
      this.pagesPagination.query.name = search
      this.pagesPagination.page = 1
      this.pagesPagination.lastPage = null
      this.pagesPagination.query.only_name = true
      this.fetchThematicPagesData()
    },

    loadMoreThematicPages () {
      this.isLoadingMorePages = true

      if (this.pagesPagination.lastPage) {
        if (this.pagesPagination.lastPage > this.pagesPagination.page) {
          this.pagesPagination.page += 1
        } else {
          this.isLoadingMorePages = false

          return
        }
      }

      this.fetchThematicPagesData(true)
    },

    addToHighlights (pageId) {
      if (this.formData.listHighlights.length >= this.maxHighlights) {
        this.setFeedback({ message: this.$t('pages.config:max.length.message') })

        return
      }

      const page = this.pages.find(page => page.id === pageId)

      page.active = 1

      this.formData.listHighlights.push(page)
    },

    removeFromHighlights (pageId) {
      this.formData.listHighlights = this.formData.listHighlights.filter(page => page.id !== pageId)
    },

    moveUpHighlight (pageId) {
      const index = this.formData.listHighlights.findIndex(page => page.id === pageId)

      if (index > 0) {
        const swap = this.formData.listHighlights[index - 1]

        this.$set(this.formData.listHighlights, (index - 1), this.formData.listHighlights[index])
        this.$set(this.formData.listHighlights, index, swap)
      }
    },

    moveDownHighlight (pageId) {
      const index = this.formData.listHighlights.findIndex(page => page.id === pageId)

      if (index < (this.formData.listHighlights.length - 1)) {
        const swap = this.formData.listHighlights[index + 1]

        this.$set(this.formData.listHighlights, (index + 1), this.formData.listHighlights[index])
        this.$set(this.formData.listHighlights, index, swap)
      }
    },

    saveHeaderTexts () {
      this.setFetching(true)

      this.attemptUpdateThematicPagesHeader({
        title: this.headerData.title,
        subtitle: this.headerData.subtitle
      })
        .then(() => {
          this.setFeedback({ message: this.$t('pages.config:feedback.success') })

          return this.fetchSettings()
        })
        .catch(() => {
          this.setFeedback({ message: this.$t('pages.config:feedback.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    updateHighlights () {
      this.$v.formData.$touch()

      if (this.$v.formData.$invalid) {
        this.setFeedback({ message: this.$t('pages.config:feedback.error') })

        return
      }

      const payload = {
        ordenation: this.formData.ordenation,
        title: this.formData.title,
        description: this.formData.description,
        thematicPages: this.formData.ordenation === 'manual'
          ? this.formData.listHighlights.map((page, index) => ({
            thematicPageId: page.id,
            highlightPosition: index
          }))
          : []
      }

      this.setFetching(true)

      this.attemptUpdateThematicPagesHighlights(payload)
        .then(() => {
          this.setFeedback({ message: this.$t('pages.config:feedback.success') })

          return this.fetchSettings()
        })
        .catch((error) => {
          this.setFeedback({ message: this.resolveHighlightError(error) })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    resolveHighlightError (error) {
      const code = error && error.response && error.response.data && error.response.data.error
        ? error.response.data.error.message
        : null

      return HIGHLIGHT_ERROR_MESSAGES[code]
        ? this.$t(HIGHLIGHT_ERROR_MESSAGES[code])
        : this.$t('pages.config:feedback.error')
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('thematicPages:header.title')"
      :description="$t('thematicPages:header.description')"
      background="/assets/images/themes/default/png/partners_banner.png"
    >
      <Tabs
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>

    <div class="center pages-config">
      <Tabs
        :links="subTabLinks"
        :index-active="activeSubTab"
        @tabChange="activeSubTab = $event"
      />

      <div
        v-show="activeSubTab === 0"
        class="pages-config__texts"
      >
        <p class="pages-config__support">
          {{ $t('pages.config:header.support') }}
        </p>

        <InputField
          v-model="headerData.title"
          type="text"
          :counter="100"
          :label="$t('pages.config:header.title.label')"
          :under-description="$t('pages.config:header.title.helper')"
          :validation="$v.headerData.title"
          :is-round-value-number="false"
        />

        <InputField
          v-model="headerData.subtitle"
          type="text"
          :counter="500"
          :label="$t('pages.config:header.subtitle.label')"
          :under-description="$t('pages.config:header.subtitle.helper')"
          :validation="$v.headerData.subtitle"
          :is-round-value-number="false"
        />

        <FormSection :title="$t('pages.config:header.preview.label')">
          <ThematicPageHeaderPreview
            :title="headerData.title"
            :subtitle="headerData.subtitle"
          />
        </FormSection>

        <Action
          primary
          fixed-width
          type="button"
          :text="$t('global:save')"
          class="btn-save"
          @click="saveHeaderTexts"
        />
      </div>

      <div
        v-show="activeSubTab === 1"
        class="pages-config_highlights"
      >
        <FormSection :title="$t('pages.config:texts.title')">
          <p class="pages-config__support">
            {{ $t('pages.config:section.support') }}
          </p>

          <InputField
            v-model="formData.title"
            type="text"
            :counter="100"
            :label="$t('pages.config:title.label')"
            :under-description="$t('pages.config:title.helper')"
            :validation="$v.formData.title"
            :is-round-value-number="false"
          />

          <InputField
            v-model="formData.description"
            type="text"
            :counter="500"
            :label="$t('pages.config:description.label')"
            :under-description="$t('pages.config:description.helper')"
            :validation="$v.formData.description"
            :is-round-value-number="false"
          />
        </FormSection>

        <FormSection class="ordenation-section">
          <p class="ordenation-section__title">
            {{ $t('pages.config:ordenation.title') }}
          </p>

          <Radio
            v-model="formData.ordenation"
            :items="orderOptions"
            horizontal
          />
        </FormSection>

        <div
          v-if="formData.ordenation === 'manual'"
          class="link-table"
        >
          <div class="linkable-items">
            <Datatable
              :is-loading-more="isLoadingMorePages"
              :items="availablePages"
              hide-horizontal-line
              has-infinite-scroll
              has-fade
              empty-message="no_result"
              @loadMore="loadMoreThematicPages"
            >
              <template slot="thead">
                <th
                  class="th"
                  width="300"
                >
                  {{ $t('pages.config:eligible.header') }}
                </th>
                <th
                  class="th"
                  width="40"
                />
              </template>

              <template slot="tsearch">
                <FilterListSearch
                  slot="search"
                  class="filter-search"
                  :prepend-label="$t('pages.config:search.label')"
                  :hide-error-details="true"
                  @search="searchThematicPage"
                />
              </template>

              <template
                slot="tbody"
                slot-scope="props"
              >
                <tr
                  :key="props.index"
                  @click="addToHighlights(props.item.id)"
                >
                  <td class="td td-full">
                    <span class="td-text bolder">{{ props.item.name }}</span>
                    <br>
                    <span class="td-text td-small">
                      {{ props.item.thematicPageType ? props.item.thematicPageType.name : '' }}
                    </span>

                    <br>
                    <span class="td-text td-small td-show-on-portal">
                      Exibir no portal:
                      <strong>{{ Boolean(props.item.showOnVitrine) ? 'Sim' : 'Não' }}</strong>
                    </span>
                  </td>
                  <td class="td td-button td-small text-right">
                    <Action
                      type="button"
                      icon="keyboard_arrow_right"
                      flat
                    />
                  </td>
                </tr>
              </template>
            </Datatable>
          </div>

          <Divisor orientation="vertical" />

          <div class="linked-items">
            <Datatable
              has-fade
              drag
              :items="formData.listHighlights"
              hide-horizontal-line
            >
              <template slot="thead">
                <th class="th">
                  {{ $t('pages.config:highlights.header') }}
                </th>
                <th
                  class="th"
                  width="40"
                />
              </template>
              <template
                slot="tbody"
                slot-scope="props"
              >
                <tr
                  v-if="props.item.active"
                  :key="props.index"
                  class="tr-draggable"
                >
                  <td class="td td-drag">
                    <Icon
                      name="drag"
                      size="is-small"
                    />
                  </td>
                  <td class="td td-full">
                    <span class="td-text bolder">{{ props.item.name }}</span>
                  </td>
                  <td class="td td-button td-small text-right">
                    <Action
                      class="order-action"
                      type="button"
                      icon="arrow_drop_down"
                      flat
                      @click="moveDownHighlight(props.item.id)"
                    />
                    <Action
                      class="order-action"
                      type="button"
                      icon="arrow_drop_up"
                      flat
                      @click="moveUpHighlight(props.item.id)"
                    />
                    <Action
                      class="order-action"
                      type="button"
                      icon="close"
                      flat
                      @click="removeFromHighlights(props.item.id)"
                    />
                  </td>
                </tr>
              </template>
            </Datatable>
          </div>
        </div>

        <Action
          primary
          fixed-width
          type="button"
          :text="$t('global:save')"
          class="btn-save"
          @click="updateHighlights"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pages-config {
  padding: 10px;
  display: flex;
  flex-direction: column;

  .datatable-wrapper {
    padding-top: 35px;
  }

  table .td {
    border: none;
  }

  .btn-save {
    align-self: center;
    margin-top: 15px;
  }

  .pages-config__support {
    margin: 16px 0 24px;
    color: #6a6c72;
    text-align: center;
  }

  .pages-config__texts {
    display: flex;
    flex-direction: column;
    padding-top: 40px;
    max-width: 720px;
    width: 100%;
    margin: 0 auto;
  }

  .pages-config_highlights {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 40px;

    .link-table {
      width: 100%;
    }

    .form-section {
      width: 100%;

      .form-section-title {
        text-align: center;
      }
    }

    .ordenation-section ::v-deep .form-item-radio {
      text-align: center;
    }

    .ordenation-section__title {
      font-family: 'Lato';
      font-size: 1.8em;
      font-weight: bold;
      letter-spacing: 0.9px;
      color: rgba(0, 0, 0, 0.5);
      margin-bottom: 10px;
      text-align: center;
    }

    .td-show-on-portal {
      font-size: 1.3em;

      strong {
        font-weight: bold;
      }
    }
  }
}
</style>
